import { AxiosMethods } from '@setvi/shared/enums';
import { axiosHelper } from '@setvi/shared/services';

import type { CoverDefaults, PrintOrderItem } from '../types';
import { buildCoverFieldsBody, parseMachineNumbers } from '../utils';

// v2.0 ShoppingCart endpoint. Hits ADMIN_API_BASE_URL
// (https://devapi.setvi.com in develop).
const SHOPPING_CART_ENDPOINT = '/api/v2.0/ShoppingCart';

export interface ShoppingCartCompletedResource {
  Id: number;
  SessionId: string | null;
  ResourceId: number | null;
  SourceType: number | null;
  SourceTypeName: string | null;
  DynamicPdfType: number | null;
  DynamicPdfTypeName: string | null;
  FileName: string | null;
  BlobPath: string | null;
  DownloadURL: string | null;
  DownloadURLWithSas: string | null;
  SizeBytes: number | null;
  CreatedUtc: string | null;
}

// A generated dynamic PDF on a saved cart line — QR sheet (Type 2) or cover
// page (Type 1). Carries the info needed to restore the customization + preview.
export interface ShoppingCartDynamicPdf {
  Id: number;
  Type: number;
  TypeName: string | null;
  ResourceId: number;
  FileName: string | null;
  BlobPath: string | null;
  DownloadURL: string | null;
  DownloadURLWithSas: string | null;
  CoverPageName: string | null;
  MachineId: string | null;
  QrUrl: string | null;
  // Cover: the details the rep entered when the cover was generated.
  CoverContactName: string | null;
  CoverContactTitle: string | null;
  CoverPhoneNumber: string | null;
  CoverEmailAddress: string | null;
  CoverWebsite: string | null;
  HasCoverLogo: boolean;
  /** The uploaded logo, base64 as it was sent. Null when none was uploaded. */
  LogoBase64?: string | null;
  CreatedUtc: string | null;
}

export interface ShoppingCartDetail {
  Id: number;
  ShoppingCartId: number;
  ResourceId: number;
  Filename: string;
  IsStatic: boolean;
  LineNumber: number | null;
  LineId: number | null;
  Sku: string | null;
  ProductName: string | null;
  Description: string | null;
  Quantity: number | null;
  UnitPrice: number | null;
  Price: number | null;
  LineSubTotal: number | null;
  LineStatus: string | null;
  PrintFileUrl: string | null;
  UploadedUrl: string | null;
  UploadFilename: string | null;
  ThumbnailUrl: string | null;
  EstimatedShipDate: string | null;
  DownloadURL: string | null;
  DownloadURLWithSas: string | null;
  ResourceThumbnailURL: string | null;
  ResourceThumbnailURLWithSas: string | null;
  DynamicPdfs: ShoppingCartDynamicPdf[] | null;
  CompletedResources: ShoppingCartCompletedResource[] | null;
}

export interface ShoppingCartAddress {
  Name?: string | null;
  AddressLine1?: string | null;
  AddressLine2?: string | null;
  AddressLine3?: string | null;
  City?: string | null;
  State?: string | null;
  ZipCode?: string | null;
  Country?: string | null;
  PhoneNumber?: string | null;
  Email?: string | null;
}

export interface ShoppingCartData {
  Id: number;
  SessionId: string;
  UserID: number;
  CompanyID: number;
  Status: number;
  StatusName: string | null;
  IsEditable: boolean;
  Url: string | null;
  Expires: string | null;
  IsError: boolean;
  Messages: string[] | null;
  ReferenceCode: string | null;
  OrderNumber: string | null;
  OrderStatus: string | null;
  OrderDate: string | null;
  CurrencyCode: string | null;
  OrderTotal: number | null;
  OrderSubTotal: number | null;
  Tax: number | null;
  ShippingAndHandling: number | null;
  OrderDiscount: number | null;
  Carrier: string | null;
  PoNumber: string | null;
  Comments: string | null;
  DesiredShipDate: string | null;
  OrderUserEmail: string | null;
  OrderUserName: string | null;
  PropagoCompanyId: number | null;
  ShippingAddress: ShoppingCartAddress | null;
  BillingAddress: ShoppingCartAddress | null;
  UnassignedDynamicPdfs: ShoppingCartDynamicPdf[] | null;
  UnassignedCompletedResources: ShoppingCartCompletedResource[] | null;
  CompletedDate: string | null;
  /** The rep's profile details, the fallback for a cover with no details. */
  CoverPageDefaults: CoverDefaults | null;
  Details: ShoppingCartDetail[];
}

export interface ShoppingCartApiResponse {
  Status: string;
  Data: ShoppingCartData;
  Error: string | null;
}

interface ShoppingCartDetailInput {
  ResourceId: number;
  Filename: string;
  IsStatic: boolean;
  // Dynamic PDF: 0 = none, 1 = cover page, 2 = QR code, 3 = location.
  DynamicPdfType: number;
  // QR: the generated /dynamicqrcode FileName. Empty otherwise.
  DynamicPdfFileName: string;
  // Cover: the selected cover page name. Empty otherwise.
  CoverPageName: string;
  // QR: the machine node ids (same format sent to /dynamicqrcode). Empty
  // otherwise.
  NodeIds: string[];
  // Location: the selected location id. Empty otherwise.
  LocationId: string;
  // Cover: the details the rep entered, the same fields `dynamiccoverpage`
  // takes. A field is left out to fall back to the profile, sent as '' to
  // print blank.
  ContactName?: string;
  ContactTitle?: string;
  PhoneNumber?: string;
  EmailAddress?: string;
  Website?: string;
  LogoBase64?: string;
}

/** How a cart line's generated PDF is classified on the server. */
export enum DynamicPdfType {
  None = 0,
  Cover = 1,
  Qr = 2,
  Location = 3
}

/**
 * An item carries at most one customization, so its type and the filename of
 * the PDF that was generated for it are decided together.
 */
const getDynamicPdf = (item: PrintOrderItem) => {
  if (item.addQr)
    return { type: DynamicPdfType.Qr, fileName: item.qrFileName ?? '' };
  if (item.addCover)
    return { type: DynamicPdfType.Cover, fileName: item.coverFileName ?? '' };
  if (item.addLocation)
    return {
      type: DynamicPdfType.Location,
      fileName: item.locationFileName ?? ''
    };
  return { type: DynamicPdfType.None, fileName: '' };
};

const toCartDetail = (item: PrintOrderItem): ShoppingCartDetailInput => {
  const dynamicPdf = getDynamicPdf(item);

  return {
    ResourceId: item.resource.ID,
    Filename: item.resource.FileName ?? item.resource.Name,
    IsStatic: false,
    DynamicPdfType: dynamicPdf.type,
    DynamicPdfFileName: dynamicPdf.fileName,
    CoverPageName: item.addCover ? (item.coverTemplateId ?? '') : '',
    NodeIds: item.addQr ? [parseMachineNumbers(item.qrNumbers).join(',')] : [],
    LocationId: item.addLocation ? (item.locationId ?? '') : '',
    ...buildCoverFieldsBody(item.coverFields)
  };
};

export interface SyncShoppingCartInput {
  /** Empty string on the first call; reuse the SessionId the API returns. */
  sessionId: string | null;
  items: PrintOrderItem[];
}

/**
 * Create / update the server shopping cart with the current set of items.
 * Resolves with the cart data (carrying `Id` + `SessionId` to reuse on later
 * calls); rejects when the API reports an error so the caller can skip the
 * local update.
 */
const syncShoppingCart = async ({
  sessionId,
  items
}: SyncShoppingCartInput): Promise<ShoppingCartData> => {
  const response: ShoppingCartApiResponse = await axiosHelper({
    endpoint: SHOPPING_CART_ENDPOINT,
    method: AxiosMethods.POST,
    baseURL: process.env.ADMIN_API_BASE_URL,
    body: {
      SessionId: sessionId ?? '',
      Details: items.map(toCartDetail)
    }
  });

  const data = response?.Data;
  if (!data || response.Error || data.IsError) {
    throw new Error(
      response?.Error ||
        data?.Messages?.join(' ') ||
        'Shopping cart update failed.'
    );
  }

  return data;
};

/** Delete the whole server cart (on cancel or when the last item is removed). */
const deleteShoppingCart = (id: number): Promise<unknown> =>
  axiosHelper({
    endpoint: `${SHOPPING_CART_ENDPOINT}/${id}`,
    method: AxiosMethods.DELETE,
    baseURL: process.env.ADMIN_API_BASE_URL
  });

export interface CheckoutResult {
  RedirectUrl?: string | null;
  Expires?: string | null;
  SessionId?: string | null;
  ReferenceCode?: string | null;
  Cart?: ShoppingCartData | null;
}

interface CheckoutApiResponse {
  Status: string;
  Data: CheckoutResult | null;
  Error: string | null;
}

// Submit the cart for print (checkout). Resolves with the checkout result,
// which carries the external RedirectUrl to open; rejects when the API reports
// an error so callers don't treat a failed checkout as success.
const checkoutShoppingCart = async (id: number): Promise<CheckoutResult> => {
  const response: CheckoutApiResponse = await axiosHelper({
    endpoint: `${SHOPPING_CART_ENDPOINT}/${id}/checkout`,
    method: AxiosMethods.POST,
    baseURL: process.env.ADMIN_API_BASE_URL
  });

  const data = response?.Data;
  if (!data || response.Error) {
    throw new Error(response?.Error || 'Shopping cart checkout failed.');
  }

  return data;
};

// React Query mutation factories (used via useSubscribedMutation, matching the
// app's Services/Mutations convention).
export const syncShoppingCartMutation = () => ({
  mutationFn: syncShoppingCart
});

export const deleteShoppingCartMutation = () => ({
  mutationFn: deleteShoppingCart
});

export const checkoutShoppingCartMutation = () => ({
  mutationFn: checkoutShoppingCart
});

export enum ShoppingCartQueryKey {
  Active = 'print_order_shopping_cart_active',
  ById = 'print_order_shopping_cart_by_id'
}

// Fetch the user's active (saved) cart, used to preselect resources when the
// Resources page opens. Returns null when the user has no active cart.
export const getActiveShoppingCartQuery = () => ({
  queryKey: [ShoppingCartQueryKey.Active],
  queryFn: async (): Promise<ShoppingCartData | null> => {
    const response: ShoppingCartApiResponse = await axiosHelper({
      endpoint: `${SHOPPING_CART_ENDPOINT}/active`,
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL
    });
    return response?.Data ?? null;
  }
});

// Fetch a specific cart by id (reorder flow — the id comes from the URL).
export const getShoppingCartByIdQuery = (id: string | number) => ({
  queryKey: [ShoppingCartQueryKey.ById, id],
  queryFn: async (): Promise<ShoppingCartData | null> => {
    const response: ShoppingCartApiResponse = await axiosHelper({
      endpoint: `${SHOPPING_CART_ENDPOINT}/${id}`,
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL
    });
    return response?.Data ?? null;
  }
});
